export const CHAIR_CAPABILITIES = [
  {
    key: "pedia",
    label: "Pedia-ready",
    shortLabel: "Pedia",
    description: "Sized and supplied for children, anxious kids, and parent-assisted visits.",
  },
  {
    key: "surgery",
    label: "Surgery-ready",
    shortLabel: "Surgery",
    description: "Reserved for extraction, implant, and longer surgical setups.",
  },
  {
    key: "ortho",
    label: "Ortho-ready",
    shortLabel: "Ortho",
    description: "Good for adjustments, bonding, debonding, and wire-change flow.",
  },
  {
    key: "xray_adjacent",
    label: "X-ray adjacent",
    shortLabel: "X-ray",
    description: "Near imaging, useful when the visit often needs radiographs.",
  },
  {
    key: "wheelchair_friendly",
    label: "Accessible",
    shortLabel: "Access",
    description: "Easier entry, transfer, and companion movement around the chair.",
  },
];

const CAPABILITY_KEYS = new Set(CHAIR_CAPABILITIES.map((item) => item.key));
const PEDIA_TERMS = /\b(pedia|pediatric|paediatric|pedo|child|children|kid|kids)\b/i;
const PEDIA_AGE_YEARS = 12;
const PEDIA_EXTRA_MINUTES = 15;

export function normalizeChairCapabilities(value) {
  let raw = value;
  if (raw == null) return [];
  if (typeof raw === "string") {
    raw = raw.trim().startsWith("[")
      ? safeJsonParse(raw, [])
      : raw.split(",");
  } else if (!Array.isArray(raw) && typeof raw === "object") {
    raw = Object.entries(raw)
      .filter(([, enabled]) => Boolean(enabled))
      .map(([key]) => key);
  }
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const out = [];
  for (const item of raw) {
    const key = String(item || "").trim();
    if (!CAPABILITY_KEYS.has(key) || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
}

export function hasChairCapability(chair, capabilityKey) {
  if (!chair || !capabilityKey) return false;
  const capabilities = normalizeChairCapabilities(
    chair.chair_capabilities ?? chair.capabilities ?? chair.capability_tags,
  );
  return capabilities.includes(capabilityKey);
}

export function isPediaSensitiveBooking({
  patient = null,
  procedure = null,
  asOf = new Date(),
  pediaAgeYears = PEDIA_AGE_YEARS,
} = {}) {
  const age = ageInYears(patient?.date_of_birth ?? patient?.birth_date ?? patient?.dob, asOf);
  if (age != null && age < pediaAgeYears) return true;

  const procedureText = [
    procedure?.name,
    procedure?.code,
    procedure?.short_code,
    procedure?.category,
    procedure?.service_category,
  ].filter(Boolean).join(" ");

  return PEDIA_TERMS.test(procedureText);
}

export function getPediaChairWarning({
  patient = null,
  procedure = null,
  chair = null,
  asOf = new Date(),
  pediaAgeYears = PEDIA_AGE_YEARS,
} = {}) {
  if (!chair || !isPediaSensitiveBooking({ patient, procedure, asOf, pediaAgeYears })) {
    return null;
  }
  if (hasChairCapability(chair, "pedia")) return null;

  return {
    title: "Pedia chair mismatch",
    message: `${chair.name || "This chair"} is not marked pedia-ready. Consider a pedia chair or add setup time before booking.`,
    suggestedExtraMinutes: PEDIA_EXTRA_MINUTES,
  };
}

export function getChairCapabilityMeta(key) {
  return CHAIR_CAPABILITIES.find((item) => item.key === key) || null;
}

function ageInYears(dob, asOf) {
  if (!dob) return null;
  const birth = new Date(dob);
  const today = asOf instanceof Date ? asOf : new Date(asOf);
  if (Number.isNaN(birth.getTime()) || Number.isNaN(today.getTime())) return null;
  let age = today.getFullYear() - birth.getFullYear();
  const birthdayPassed = today.getMonth() > birth.getMonth()
    || (today.getMonth() === birth.getMonth() && today.getDate() >= birth.getDate());
  if (!birthdayPassed) age -= 1;
  return age;
}

function safeJsonParse(value, fallback) {
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}
